import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import VerifiedBadge from "@/components/app/VerifiedBadge";
import { PushSettingsToggle } from "@/components/app/PushOptInBanner";
import { PaymentLinkSettings } from "@/components/app/PaymentLinkSettings";
import { StripeConnectCard } from "@/components/app/StripeConnectCard";
import { MapPin, Stethoscope, Calendar, Star, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { findCountry, formatMoney } from "@/lib/i18n";

export default function PublicProfile() {
  const { userId } = useParams();
  const { user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [recs, setRecs] = useState([]);
  const [following, setFollowing] = useState(false);
  const [recDialog, setRecDialog] = useState(false);
  const [recForm, setRecForm] = useState({ rating: 5, comment: "" });

  const load = async () => {
    try {
      const [{ data: p }, { data: ps }, { data: rs }, { data: fs }] = await Promise.all([
        api.get(`/users/${userId}`),
        api.get(`/posts/user/${userId}`),
        api.get(`/recommendations/${userId}`).catch(() => ({ data: [] })),
        api.get(`/follow/status/${userId}`).catch(() => ({ data: { following: false } })),
      ]);
      setProfile(p);
      setPosts(ps);
      setRecs(rs);
      setFollowing(fs.following);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const toggleFollow = async () => {
    try {
      if (following) {
        await api.delete(`/follow/${userId}`);
        setFollowing(false);
      } else {
        await api.post(`/follow/${userId}`);
        setFollowing(true);
      }
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const submitRec = async (e) => {
    e.preventDefault();
    try {
      await api.post("/recommendations", { doctor_id: userId, ...recForm });
      toast.success("Merci pour votre recommandation !");
      setRecDialog(false);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  if (!profile) return <div className="p-12 text-center text-slate-500">Chargement…</div>;

  const c = findCountry(profile.country);
  const isDoctor = profile.role === "doctor";
  const isMe = me?.user_id === profile.user_id;

  return (
    <div className="max-w-4xl mx-auto pb-12">
      {/* Cover */}
      <div className="h-44 sm:h-56 bg-gradient-to-br from-[#618DB8] via-[#4A78A3] to-[#2A4365]" />
      <div className="px-4 sm:px-8">
        <div className="-mt-16 flex items-end justify-between flex-wrap gap-4">
          <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl border-4 border-white bg-white flex items-center justify-center text-4xl font-display font-extrabold text-[#618DB8] shadow-md">
            {profile.name.replace(/^Dr\s+/, "").charAt(0)}
          </div>
          <div className="flex gap-2 pb-2">
            {!isMe && me?.role === "patient" && isDoctor && (
              <Dialog open={recDialog} onOpenChange={setRecDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="recommend-btn"><ThumbsUp className="h-4 w-4 me-2" />Recommander</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Recommander {profile.name}</DialogTitle></DialogHeader>
                  <form onSubmit={submitRec} className="space-y-4 mt-2">
                    <div>
                      <label className="text-sm font-semibold">Note</label>
                      <div className="flex gap-1 mt-1">
                        {[1,2,3,4,5].map((n) => (
                          <button type="button" key={n} onClick={() => setRecForm({ ...recForm, rating: n })} data-testid={`rec-star-${n}`}>
                            <Star className={`h-7 w-7 ${n <= recForm.rating ? "star fill-current" : "text-slate-300"}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <Textarea placeholder="Témoignage (optionnel)" value={recForm.comment} onChange={(e) => setRecForm({ ...recForm, comment: e.target.value })} rows={4} data-testid="rec-comment-input" />
                    <Button type="submit" className="w-full btn-primary" data-testid="rec-submit-btn">Envoyer</Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
            {!isMe && (
              <Button onClick={toggleFollow} className={following ? "btn-deep" : "btn-coral"} data-testid="follow-btn">
                {following ? "Abonné" : "Suivre"}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4">
          <h1 className="font-display text-3xl font-extrabold flex items-center text-deep">
            {profile.name}
            {profile.blue_check && <VerifiedBadge size={20} />}
          </h1>
          <div className="text-slate-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            {isDoctor && profile.specialty && <span className="flex items-center gap-1"><Stethoscope className="h-3.5 w-3.5" />{profile.specialty}</span>}
            {profile.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{profile.city} {c.flag}</span>}
            {profile.avg_rating > 0 && <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 star fill-current" />{profile.avg_rating} · {profile.recommendations_count} avis</span>}
          </div>
          {profile.bio && <p className="mt-3 text-slate-700">{profile.bio}</p>}
          <div className="mt-4 flex flex-wrap gap-6 text-sm">
            <div><b className="text-deep">{profile.posts_count || 0}</b> <span className="text-slate-500">publications</span></div>
            <div><b className="text-deep">{profile.followers_count || 0}</b> <span className="text-slate-500">abonnés</span></div>
            <div><b className="text-deep">{profile.following_count || 0}</b> <span className="text-slate-500">abonnements</span></div>
            {isDoctor && <div><b className="text-deep">{formatMoney(profile.consultation_fee, profile.country)}</b> <span className="text-slate-500">/ consultation</span></div>}
          </div>
        </div>

        {/* Posts */}
        <div className="mt-10">
          <h2 className="font-display text-xl font-bold mb-4 text-deep">Publications</h2>
          {posts.length === 0 ? (
            <p className="text-slate-500 text-sm">Aucune publication.</p>
          ) : (
            <div className="space-y-3">
              {posts.map((p) => (
                <article key={p.post_id} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="text-xs text-slate-500">{new Date(p.created_at).toLocaleString("fr-FR")}</div>
                  <div className="mt-2 whitespace-pre-wrap text-slate-800">{p.content}</div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Recommendations */}
        {isDoctor && (
          <div className="mt-10">
            <h2 className="font-display text-xl font-bold mb-4 text-deep">Recommandations patients</h2>
            {recs.length === 0 ? (
              <p className="text-slate-500 text-sm">Aucune recommandation pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {recs.map((r) => (
                  <div key={r.recommendation_id} className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold">{(r.patient?.name || "?").charAt(0)}</div>
                      <div className="text-sm font-semibold">{r.patient?.name}</div>
                      <div className="ms-auto flex">
                        {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 star fill-current" />)}
                      </div>
                    </div>
                    {r.comment && <p className="mt-2 text-sm text-slate-700">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Paramètres notifications push — visible uniquement sur son propre profil */}
        {isMe && (
          <div className="mt-8 space-y-6">
            {isDoctor && <StripeConnectCard />}
            {isDoctor && <PaymentLinkSettings />}
            <div>
              <h2 className="font-display text-xl font-bold text-deep mb-3">Paramètres notifications</h2>
              <PushSettingsToggle />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
